import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Tm1EmptyState, Tm1Icon, Tm1Popover, Tm1Search, Tm1Typography } from '@tm1/design-system-react';
import classnames from 'classnames';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { FlagImage, parseCountry, usePhoneInput } from 'react-international-phone';
import { COUNTRY_CODE_US, getNonPreferredCountries, getPreferredCountries, translateCountryNames, } from './country-data-utils';
import { CountryDropdownMenuItem } from './country-dropdown-menu-item';
import { getDefaultCountries, getPhoneDataFromCountryIso } from './phone-number-utils';
import 'react-international-phone/style.css';
import './phone-number-field.scss';
export const PREFERRED_COUNTRIES = [COUNTRY_CODE_US, 'ca', 'gb', 'ie', 'au', 'nz'];
let countries;
let preferredCountries;
let nonPreferredCountries;
export const PhoneNumberField = forwardRef(({ disabled, formRef, name, label, error, onBlur, initialValue, initialCountryIso, onPhoneDataChange, lang, placeholder, selectACountryLabel, }, ref) => {
    const language = lang || 'en';
    const organizationCountryIso = initialCountryIso || COUNTRY_CODE_US;
    const defaultPhoneData = useMemo(() => getPhoneDataFromCountryIso(organizationCountryIso), [organizationCountryIso]);
    const initialPhoneNumber = initialValue
        ? {
            ...defaultPhoneData,
            phone: initialValue,
        }
        : defaultPhoneData;
    countries = countries || translateCountryNames(getDefaultCountries(), language);
    preferredCountries = preferredCountries || getPreferredCountries(countries, PREFERRED_COUNTRIES);
    nonPreferredCountries = nonPreferredCountries || getNonPreferredCountries(countries, PREFERRED_COUNTRIES, language);
    const [searchCountry, setSearchCountry] = useState('');
    const [phoneData, setPhoneData] = useState(initialPhoneNumber);
    const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
        defaultCountry: phoneData === null || phoneData === void 0 ? void 0 : phoneData.countryIso,
        value: phoneData === null || phoneData === void 0 ? void 0 : phoneData.phone,
        countries,
        disableDialCodeAndPrefix: false,
        onChange: (newPhoneData) => {
            const phone = {
                phone: newPhoneData.phone,
                countryIso: newPhoneData.country.iso2,
            };
            if (onPhoneDataChange) {
                onPhoneDataChange(phone);
            }
            setPhoneData(phone);
        },
    });
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const handleCountrySelection = (event) => {
        if (event instanceof CustomEvent && event.detail) {
            setCountry(event.detail);
            setDropdownVisible(!dropdownVisible);
        }
    };
    const handleEnterKeyPress = (event) => {
        var _a;
        if (event.key === 'Enter') {
            event.preventDefault();
            (_a = formRef === null || formRef === void 0 ? void 0 : formRef.current) === null || _a === void 0 ? void 0 : _a.requestSubmit();
        }
    };
    useImperativeHandle(ref, () => ({
        reset: () => {
            setPhoneData(defaultPhoneData);
        },
    }));
    const handleSearch = (event) => {
        var _a;
        setSearchCountry((_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.value);
    };
    return (_jsxs("div", { children: [label && (_jsx(Tm1Typography, { type: "label-helpertext-regular", className: "label", children: _jsx("label", { htmlFor: "phone-number", children: label }) })), _jsxs("div", { className: classnames('phone-number-row', {
                    disabled,
                    'with-error': !!error,
                }), children: [_jsxs("button", { type: "button", className: "select-country", onClick: (event) => {
                            event.preventDefault();
                            setDropdownVisible(!dropdownVisible);
                        }, children: [_jsx(FlagImage, { iso2: country.iso2, className: "select-country-flag", alt: country.name }), _jsx("span", { className: "sr-only", children: selectACountryLabel })] }), _jsx("input", { id: "phone-number", disabled: disabled, onChange: handlePhoneValueChange, ref: inputRef, name: "local-phone-number-field", color: "primary", placeholder: placeholder, value: inputValue, onKeyDown: handleEnterKeyPress, onBlur: onBlur, className: classnames('phone-number-input', { disabled }) }), _jsx("input", { name: name, type: "hidden", "aria-label": "search by phone number", value: JSON.stringify(phoneData) })] }), error && (_jsxs("div", { className: "error", role: "alert", children: [_jsx(Tm1Icon, { icon: "errorSolid", size: "small" }), _jsx(Tm1Typography, { type: "label-helpertext-regular", children: error })] })), dropdownVisible ? (_jsx(Tm1Popover, { padding: "none", visible: true, alignment: "start", className: "phone-number-dropdown", children: _jsxs("div", { className: "phone-number-layout", children: [_jsx("div", { className: "phone-number-dropdown-header", children: _jsx(Tm1Search, { value: searchCountry, className: "phone-number-search", onInput: handleSearch }) }), _jsxs("div", { className: "countries-dropdown", children: [preferredCountries
                                    .filter((c) => parseCountry(c).name.toLowerCase().includes(searchCountry))
                                    .map((countryData) => {
                                    const parsedCountry = parseCountry(countryData);
                                    return (_jsx(CountryDropdownMenuItem, { country: parsedCountry, onCountrySelection: handleCountrySelection }, parsedCountry.iso2));
                                }), nonPreferredCountries
                                    .filter((c) => parseCountry(c).name.toLowerCase().includes(searchCountry))
                                    .map((countryData) => {
                                    const parsedCountry = parseCountry(countryData);
                                    return (_jsx(CountryDropdownMenuItem, { country: parsedCountry, onCountrySelection: handleCountrySelection }, parsedCountry.iso2));
                                }), preferredCountries.filter((c) => parseCountry(c).name.toLowerCase().includes(searchCountry)).length ===
                                    0 &&
                                    nonPreferredCountries.filter((c) => parseCountry(c).name.toLowerCase().includes(searchCountry))
                                        .length === 0 && (_jsx(Tm1EmptyState, { className: "phone-number-empty-state", title: "No results", subtitle: "Try adjusting your search to find what you're looking for.", children: _jsx(Tm1Icon, { slot: "media", icon: "search" }) }))] })] }) })) : undefined] }));
});
